import React, { useState, useEffect, useRef } from 'react';
import { FaSearch } from 'react-icons/fa';
import { HiOutlineSearch } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { searchProducts, getProductsBySymptom } from '../../services/apiService';
import { motion, AnimatePresence } from 'framer-motion';
import { scrollToTop } from '../../utils/scrollUtils';
import { getOptimizedImageUrl } from '../../utils/imageUtils';
import { useLanguage } from '../../contexts/LanguageContext';
import PriceDisplay from './PriceDisplay';
import { trackEvent, setClickSource } from '../../services/analytics/analyticsService';

const SearchBar = ({ onClose }) => {
  const { t, language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [previewResults, setPreviewResults] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [didYouMean, setDidYouMean] = useState(null);
  const [matchedSymptom, setMatchedSymptom] = useState(null);
  const navigate = useNavigate();
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        onClose?.();
        setShowPreview(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    if (searchTerm.trim().length >= 2) {
      setIsLoading(true);
      setShowPreview(true);

      const fetchResults = async () => {
        try {
          const response = await searchProducts(searchTerm, 1, 20, '', 'random', '');
          const ms = response?.success ? (response.data.matchedSymptom || null) : null;
          setMatchedSymptom(ms);
          if (ms) {
            // The text search for a symptom word (e.g. "headache") matches
            // nothing in the German catalog — preview the symptom's products.
            const symResp = await getProductsBySymptom(ms.key, 1, 20);
            setPreviewResults(symResp?.success ? (symResp.data.products || []).slice(0, 5) : []);
            setDidYouMean(null);
          } else if (response?.success && Array.isArray(response.data.products)) {
            setPreviewResults(response.data.products.slice(0, 5));
            setDidYouMean(response.data.didYouMean || null);
          }
        } catch (error) {
          setPreviewResults([]);
          setDidYouMean(null);
          setMatchedSymptom(null);
        } finally {
          setIsLoading(false);
        }
      };

      const timer = setTimeout(fetchResults, 150);
      return () => clearTimeout(timer);
    } else {
      setPreviewResults([]);
      setShowPreview(false);
      setMatchedSymptom(null);
    }
  }, [searchTerm]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      trackEvent('search', { query: searchTerm.trim() });
      // If the query matches a known symptom, Enter goes to the symptom results
      // (a plain text search for e.g. "headache" finds nothing in the German
      // catalog). Mirrors clicking the symptom shortcut.
      if (matchedSymptom) {
        navigate(`/products?symptom=${matchedSymptom.key}`);
      } else {
        const searchParams = new URLSearchParams();
        searchParams.append('search', searchTerm);
        navigate(`/products?${searchParams.toString()}`);
      }
      onClose?.();
      setShowPreview(false);
      setTimeout(() => scrollToTop(), 100);
    }
  };

  const handlePreviewClick = (pharmacode) => {
    setClickSource('search');
    navigate(`/product/${pharmacode}`);
    onClose?.();
    setSearchTerm('');
  };

  // getOptimizedImageUrl is now imported from utils/imageUtils

  // If onClose is provided, we're in mobile view
  const isMobile = !!onClose;

  return (
    <div ref={searchRef} className="relative w-full">
      <form onSubmit={handleSearchSubmit} className="relative w-full">
        <div className="flex items-center w-full bg-white border border-gray-200 rounded-full shadow-sm focus-within:border-brand focus-within:ring-1 focus-within:ring-brand transition-all duration-300 ease-in-out">
          {/* Search Icon and Input */}
          <div className="flex-1 flex items-center px-4 py-1.5">
            <HiOutlineSearch className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 text-sm placeholder-gray-500 focus:outline-none focus:ring-0 focus:border-none bg-transparent border-none shadow-none"
              style={{ outline: 'none', boxShadow: 'none' }}
              placeholder={t('searchBar.placeholder')}
            /* The field is the entire purpose of a modal the user just chose to
               open; focusing it is what a screen-reader user expects, and
               Escape closes it. */
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={isMobile}
            />
          </div>
        </div>
      </form>

      <AnimatePresence>
        {showPreview && searchTerm.trim().length >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 mt-3 flex w-full flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-[0_30px_70px_-30px_rgba(17,24,39,0.45)]"
            style={{ maxHeight: '480px' }}
          >
            {matchedSymptom && !isLoading && (
              <button
                type="button"
                onClick={() => {
                  navigate(`/products?symptom=${matchedSymptom.key}`);
                  onClose?.();
                  setShowPreview(false);
                  setTimeout(() => scrollToTop(), 100);
                }}
                className="w-full flex-shrink-0 flex items-center gap-3 px-4 py-3 border-b border-red-50 bg-red-50/60 text-left transition-colors hover:bg-red-50"
              >
                <span className="text-xl">🩹</span>
                <span className="flex-1 text-sm">
                  <span className="font-semibold text-brand">{matchedSymptom.label}</span>
                  <span className="text-red-500"> — {t('searchBar.symptomShortcut')}</span>
                </span>
                <span className="text-brand">→</span>
              </button>
            )}
            {isLoading ? (
              <div className="p-6 text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand border-r-transparent"></div>
              </div>
            ) : previewResults.length > 0 ? (
              <motion.div
                className="py-2 divide-y divide-gray-50 overflow-y-auto flex-1 min-h-0"
                initial="hidden"
                animate="visible"
                variants={{
                  visible: {
                    transition: {
                      staggerChildren: 0.05
                    }
                  }
                }}
              >
                {previewResults.map((product) => {
                  // Get the first article from the product (search results have articles array)
                  const firstArticle = product.articles?.[0];
                  
                  // Handle different image structures:
                  // 1. Search results: article.images is directly an array
                  // 2. Other cases: product.images might be an array or article.images.F
                  let imageArray = [];
                  if (firstArticle && Array.isArray(firstArticle.images)) {
                    // Direct array from article (search results format)
                    imageArray = firstArticle.images;
                  } else if (Array.isArray(product.images)) {
                    // Direct array from product
                    imageArray = product.images;
                  } else if (firstArticle && Array.isArray(firstArticle.images?.F)) {
                    // Object with F property
                    imageArray = firstArticle.images.F;
                  }

                  const article = {
                    pharmacode: firstArticle?.pharmacode || product._id,
                    description: {
                      description: firstArticle?.description?.description || product.description?.description || 'No description available',
                      longDescription: firstArticle?.description?.longDescription || product.description?.longDescription || product.longDescription || 'No description available'
                    },
                    hasPhoto: firstArticle?.hasPhoto || product.hasPhoto || false,
                    images: {
                      F: imageArray.map(img => img.replace(/^uploads[\\/]/, ''))
                    },
                    price: firstArticle?.price ?? null
                  };

                  return (
                    <motion.div
                      key={product._id}
                      variants={{
                        hidden: { opacity: 0, y: 10 },
                        visible: { opacity: 1, y: 0 }
                      }}
                      whileHover={{ scale: 1.02 }}
                      onClick={() => handlePreviewClick(article.pharmacode)}
                      className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center space-x-4 transition-colors duration-200"
                    >
                      <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-2xl bg-gray-50 ring-1 ring-gray-100">
                        {article.hasPhoto && article.images.F.length > 0 ? (
                          <img
                            src={getOptimizedImageUrl(article.images.F) || ''}
                            alt={article.description.description}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 line-clamp-2">
                          {article.description.longDescription}
                        </p>
                        <PriceDisplay article={article} variant="compact" className="mt-1" />

                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                {didYouMean ? (
                  <div>
                    <p className="mb-3 text-gray-500">Keine Ergebnisse gefunden für "{searchTerm}"</p>
                    <div className="rounded-2xl border border-red-100 bg-red-50/60 p-5">
                      <div className="flex items-center space-x-2 mb-2">
                        <svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm font-semibold text-gray-900">
                          Meinten Sie:
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setSearchTerm(didYouMean);
                          const searchParams = new URLSearchParams();
                          searchParams.append('search', didYouMean);
                          navigate(`/products?${searchParams.toString()}`);
                          onClose?.();
                          setShowPreview(false);
                        }}
                        className="inline-flex items-center rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white shadow-[0_12px_28px_-14px_rgb(var(--brand)/0.45)] transition-all duration-300 hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-red-200 focus:ring-offset-2"
                      >
                        <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        "{didYouMean}"
                      </button>
                    </div>
                  </div>
                ) : (
                  <p>Keine Ergebnisse gefunden für "{searchTerm}"</p>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchBar;